import { multilingualFieldSchema } from "@/i18n";
import {
  AttachmentWithLegacyFields,
  hydrateAttachmentMultilingualFields,
} from "@/lib/media/attachment-multilingual";
import api from "@/lib/axiosConfig.ts";
import { PublicationStatus } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

export interface AttachmentDTO {
  where: any;
  order: any;
  //take: number;
  //skip: number;
}

export const attachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: z.string(),
  caption: multilingualFieldSchema(z.string()).optional(),
  description: multilingualFieldSchema(z.string()).optional(),
  textAlt: multilingualFieldSchema(z.string()).optional(),
  notes: multilingualFieldSchema(z.string()).nullish(),
  photoCredits: z.string().optional().default(""),
  // Prisma peut renvoyer un objet JSON ; on normalise en string pour le typage BO.
  details: z.preprocess((val) => {
    if (val == null || val === "") return val ?? null;
    if (typeof val === "string") return val;
    try {
      return JSON.stringify(val);
    } catch {
      return null;
    }
  }, z.string().optional().nullable()),
  mimeType: z.string().optional(),
  docId: z.string(),
  hdDocId: z.string().optional(),
  order: z.number().optional(),
  smallPublicUrl: z.string().optional(),
  mediumPublicUrl: z.string().optional(),
  largePublicUrl: z.string().optional(),
  publicUrl: z.string().optional(),
  copyrightSmallPublicUrl: z.string().optional().nullable(),
  copyrightMediumPublicUrl: z.string().optional().nullable(),
  copyrightLargePublicUrl: z.string().optional().nullable(),

  isPublic: z.boolean().optional(),
  published: z.boolean().optional().default(false),
  isHd: z.boolean().optional(),
  /** Clé S3 de base (inchangée par une retouche). */
  link: z.string().optional(),
  /** Recette courante ; null = jamais retouché. */
  editRecipe: z.unknown().nullable().optional(),
  mimetype: z.string().optional(),
  applyWatermark: z.boolean().optional().default(true),
  isGenericImage: z.boolean().optional(),

  // Media kind + video/embed fields
  kind: z.string().optional(), // "image" | "video" | "embed"
  videoStatus: z.string().optional(), // "" | pending | processing | ready | error
  videoPublicUrl: z.string().optional().nullable(),
  posterPublicUrl: z.string().optional().nullable(),
  videoError: z.string().optional(),
  duration: z.number().optional(),
  embedProvider: z.string().optional(), // "" | youtube | vimeo
  embedVideoId: z.string().optional(),
  embedUrl: z.string().optional(),
  videoLoop: z.boolean().optional(),
  weight: z.number().optional(),
});

export type Attachment = z.infer<typeof attachmentSchema>;

export const formImageInputSchema = z.object({
  base64Value: z.string().optional(),
  attachmentId: z.string().optional(),
});

export const ALL_ATTACHMENT_FORMATS = ["small", "medium", "large"] as const;

export type AttachmentFormat = (typeof ALL_ATTACHMENT_FORMATS)[number];

export const ATTACHMENT_FORMAT_CONVERSION = Object.freeze<
  Record<AttachmentFormat, string>
>({
  small: "petit",
  medium: "moyen",
  large: "large",
});

export const PUBLIC_ATTACHMENT_FORMATS = [
  "small",
  "medium",
] as const satisfies AttachmentFormat[];

export type PublicAttachmentFormat = Extract<
  AttachmentFormat,
  (typeof PUBLIC_ATTACHMENT_FORMATS)[number]
>;

export type AttachmentPublicUrls = Record<
  PublicAttachmentFormat,
  string | null
>;

async function getAttachments({ where, order }: AttachmentDTO) {
  const { data } = await api.post<Attachment[]>(
    `back/api/attachment`,
    {
      where,
      orderBy: order,
    },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useGetAllAttachments = ({ where, order }: AttachmentDTO) => {
  return useQuery({
    queryKey: ["artworks", "attachments", where, order],
    queryFn: () => getAttachments({ where, order }),
  });
};

export async function getAttachmentById(id: string) {
  const { data } = await api.get(`back/api/attachment/id/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export type EditSourceResponse = {
  url: string;
  isHd: boolean;
  isFirstEdit: boolean;
  link: string;
};

export async function getEditSource(id: string): Promise<EditSourceResponse> {
  const { data } = await api.get<EditSourceResponse>(
    `back/api/attachment/edit-source/${id}`,
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useGetEditSource = (id: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: ["attachment", "edit-source", id],
    queryFn: () => getEditSource(id as string),
    enabled: enabled && !!id,
  });
};

export interface UseGetAttachmentByIdOptions {
  pollWhileProcessing?: boolean;
  pollIntervalMs?: number;
}

export const useGetAttachmentById = (
  id: string,
  options?: UseGetAttachmentByIdOptions,
) => {
  const pollWhileProcessing = options?.pollWhileProcessing ?? false;
  const pollIntervalMs = options?.pollIntervalMs ?? 4000;

  return useQuery({
    queryKey: ["attachment", id],
    queryFn: () => getAttachmentById(id),
    enabled: !!id,
    // URLs S3 signées (1h) : sans staleTime, chaque remount d'observer
    // refetchait et changeait le `src` des <img> (boucle vignettes CR).
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      if (!pollWhileProcessing) return false;
      const att = query.state.data;
      if (att?.kind === "video" && att?.videoStatus === "processing") {
        return pollIntervalMs;
      }
      return false;
    },
  });
};

export async function getAttachmentsByDocId(id: string | undefined) {
  if (!id) {
    return [];
  }
  const { data } = await api.get<Attachment[]>(`back/api/attachment/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export interface UseGetAttachmentsByDocIdOptions {
  /** When set, poll while the attachment at this position is a processing video. */
  pollPosition?: string;
  /** Poll while any video on the document is still processing. */
  pollWhileAnyVideoProcessing?: boolean;
  pollIntervalMs?: number;
}

export const useGetAttachmentsByDocId = (
  id: string | undefined,
  options?: UseGetAttachmentsByDocIdOptions,
) => {
  const pollPosition = options?.pollPosition;
  const pollWhileAnyVideoProcessing =
    options?.pollWhileAnyVideoProcessing ?? false;
  const pollIntervalMs = options?.pollIntervalMs ?? 4000;

  return useQuery({
    queryKey: ["attachment", { docId: id }],
    queryFn: () => getAttachmentsByDocId(id),
    enabled: !!id,
    // Cf. useGetAttachmentById : évite le refetch à chaque remount (URLs signées).
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const attachments = query.state.data;
      if (pollWhileAnyVideoProcessing) {
        const hasProcessingVideo = attachments?.some(
          (attachment) =>
            attachment.kind === "video" &&
            attachment.videoStatus === "processing",
        );
        if (hasProcessingVideo) return pollIntervalMs;
      }
      if (!pollPosition) return false;
      const att = attachments?.find((a) => a.position === pollPosition);
      if (att?.kind === "video" && att?.videoStatus === "processing") {
        return pollIntervalMs;
      }
      return false;
    },
  });
};

export interface UpdateAttachmentDTO {
  id: string;
  attachment: AttachmentUpdatePayload;
}

/** Prisma-safe fields for PUT /attachment/:id (no signed URLs, relations, audit columns). */
export type AttachmentUpdatePayload = {
  name: string;
  position: string;
  docId: string;
  caption?: Attachment["caption"];
  textAlt?: Attachment["textAlt"];
  description?: Attachment["description"];
  notes?: Attachment["notes"];
  photoCredits: string;
  details: string;
  order?: number;
  published: boolean;
  isHd: boolean;
  applyWatermark: boolean;
  isGenericImage: boolean;
  videoLoop?: boolean;
};

function attachmentDetailsToString(details: unknown): string {
  if (typeof details === "string") return details;
  if (details != null) return JSON.stringify(details);
  return "";
}

export function buildAttachmentUpdatePayload(
  existing: AttachmentWithLegacyFields,
  overrides: Partial<AttachmentUpdatePayload> = {},
): AttachmentUpdatePayload {
  const hydrated = hydrateAttachmentMultilingualFields(existing);

  return {
    name: overrides.name ?? existing.name ?? "",
    position: overrides.position ?? existing.position ?? "",
    docId: overrides.docId ?? existing.docId ?? "",
    caption:
      overrides.caption !== undefined ? overrides.caption : hydrated.caption,
    textAlt:
      overrides.textAlt !== undefined ? overrides.textAlt : hydrated.textAlt,
    description:
      overrides.description !== undefined
        ? overrides.description
        : hydrated.description,
    notes: overrides.notes !== undefined ? overrides.notes : existing.notes ?? null,
    photoCredits: overrides.photoCredits ?? existing.photoCredits ?? "",
    details: overrides.details ?? attachmentDetailsToString(existing.details),
    order: overrides.order ?? existing.order,
    published: overrides.published ?? existing.published ?? false,
    isHd: overrides.isHd ?? existing.isHd ?? false,
    applyWatermark: overrides.applyWatermark ?? existing.applyWatermark ?? true,
    isGenericImage:
      overrides.isGenericImage ?? existing.isGenericImage ?? false,
    videoLoop: overrides.videoLoop ?? existing.videoLoop ?? false,
  };
}

export async function updateAttachment({
  id,
  attachment,
}: UpdateAttachmentDTO) {
  const { data } = await api.put(
    `back/api/attachment/${id}`,
    {
      attachment,
    },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useUpdateAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachment"] });
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

async function reorderAttachments(items: { id: string; order: number }[]) {
  const { data } = await api.post(
    `back/api/attachment/reorder`,
    { items },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useReorderAttachments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderAttachments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachment"] });
    },
  });
};

export interface CreateGenericAttachmentPayload {
  docId: string;
  position: string;
  name?: string;
  displayName?: string;
  caption?: Record<string, string>;
  textAlt?: Record<string, string>;
  photoCredits?: string;
  notes?: Record<string, string> | null;
  published?: boolean;
  order?: number;
}

export async function createGenericImageAttachment(
  payload: CreateGenericAttachmentPayload,
): Promise<Attachment> {
  const { data } = await api.post<Attachment>(
    `back/api/attachment/create-generic`,
    payload,
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useCreateGenericImageAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createGenericImageAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachment"] });
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

async function deleteAttachment(id: string) {
  const { data } = await api.delete(`back/api/attachment/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export const useDeleteAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachment"] });
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

async function deleteHdAttachment(id: string) {
  const { data } = await api.delete(`back/api/attachment/hd/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export const useDeleteHdAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHdAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachment"] });
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

async function getPublicUrl(id: string | undefined) {
  if (!id) {
    return null;
  }
  return await api.get(`back/api/attachment/share/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
}

export const useGetPublicUrl = (id: string | undefined) => {
  return useQuery({
    queryKey: ["attachment", id],
    queryFn: () => getPublicUrl(id),
    enabled: !!id,
  });
};

export async function getAttachmentBiggestWeight(
  attachmentId: string,
): Promise<number | null> {
  if (!attachmentId) {
    return null;
  }
  const { data } = await api.get<number>(
    `back/api/attachment/weight/${attachmentId}`,
    {
      headers: {
        instance: localStorage.getItem("instance"),
        "Cache-Control": "no-cache",
      },
    },
  );
  return typeof data === "number" ? data : null;
}


export interface DuplicateAttachmentToDocumentPayload {
  sourceAttachmentId: string;
  targetDocId: string;
  position: string;
  published?: boolean;
  name?: string;
}

export async function duplicateAttachmentToDocument(
  payload: DuplicateAttachmentToDocumentPayload,
): Promise<Attachment> {
  const { data } = await api.post<Attachment>(
    "back/api/attachment/duplicate-to-document",
    payload,
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useDuplicateAttachmentToDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: duplicateAttachmentToDocument,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["attachment", { docId: variables.targetDocId }],
      });
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

export interface CopyAttachmentToPositionPayload {
  sourceAttachmentId: string;
  targetPosition: string;
}

export async function copyAttachmentToPosition(
  payload: CopyAttachmentToPositionPayload,
): Promise<Attachment> {
  const { data } = await api.post<Attachment>(
    "back/api/attachment/copy-to-position",
    payload,
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useCopyAttachmentToPosition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: copyAttachmentToPosition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachment"] });
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

export interface CreateEmbedPayload {
  url: string;
  docId: string;
  position: string;
  instanceId?: string;
  order?: number;
  published?: boolean;
  /** Single-slot positions (CMS cover media): replace any existing attachment at this position. */
  unique?: boolean;
  caption?: Record<string, string>;
  textAlt?: Record<string, string>;
  description?: Record<string, string>;
  videoLoop?: boolean;
}

export async function createEmbedAttachment(
  payload: CreateEmbedPayload,
): Promise<Attachment> {
  const { data } = await api.post<Attachment>(
    `back/api/attachment/create/embed`,
    payload,
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useCreateEmbedAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEmbedAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachment"] });
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

export interface CreateVideoPlaceholderPayload {
  name: string;
  displayName?: string;
  docId: string;
  position: string;
  instanceId?: string;
  order?: number;
  published?: boolean;
  /** Single-slot positions (CMS cover media): replace any existing attachment at this position. */
  unique?: boolean;
  caption?: Record<string, string>;
  textAlt?: Record<string, string>;
  description?: Record<string, string>;
  photoCredits?: string;
  notes?: Record<string, string> | null;
  videoLoop?: boolean;
}

export async function createVideoPlaceholder(
  payload: CreateVideoPlaceholderPayload,
): Promise<Attachment> {
  const { data } = await api.post<Attachment>(
    `back/api/attachment/create/video`,
    payload,
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

/** Attachments for overview preview / front parity — not dashboard table thumbnails. */
export function shouldIncludeAttachment(
  attachment: Attachment,
  _publicationStatus?: PublicationStatus,
) {
  if (attachment.isGenericImage === true) {
    return true;
  }
  return attachment.published === true;
}
